import { DatePicker, Divider, Select } from "antd";
import Title from "antd/es/typography/Title";
import dayjs, { Dayjs } from "dayjs";
import { useFormikContext } from "formik";
import React, { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReportItem, useGetAllMaterialsQuery } from "shared/api";
import { REPORT_DATE_TIME_FORMAT, REPORT_TIME_FORMAT } from "shared/constants";
import { useDebouncedValue } from "shared/hooks";
import { Colors } from "shared/themes";
import { ExecuteReportValues } from "shared/types";
import { ReportFormatSelect } from "./ReportFormatSelect";

interface ConsumptionFilterProps {
  clearPdfUrl?: () => void;
  selectedReport?: ReportItem;
}

//  Consumption report: a single material plus the period it was consumed in.
//  Payload -> material_id, timestamp_start, timestamp_end
const ConsumptionFilter: FC<ConsumptionFilterProps> = ({
  clearPdfUrl,
  selectedReport,
}) => {
  const { t } = useTranslation("", { keyPrefix: "executeReports" });

  const { values, setFieldValue } = useFormikContext<ExecuteReportValues>();

  const [materialSearch, setMaterialSearch] = useState("");
  const debouncedMaterialSearch = useDebouncedValue(materialSearch, 400);

  const { data: materials, isFetching: isFetchingMaterials } =
    useGetAllMaterialsQuery({
      limit: 200,
      name__icontains: debouncedMaterialSearch || undefined,
    });

  const adaptedMaterials = useMemo(() => {
    return (
      materials?.results?.map((material) => ({
        label: material.name,
        value: material.id,
      })) || []
    );
  }, [materials]);

  const handleMaterialChange = (value: number | null) => {
    setFieldValue("material_id", value ?? null);

    if (clearPdfUrl) {
      clearPdfUrl();
    }
  };

  const handleDateChange = (
    field: "timestamp_start" | "timestamp_end",
    val: Dayjs | null
  ) => {
    setFieldValue(field, val ? val.toISOString() : null);
  };

  return (
    <>
      <Divider className="my-1.5" style={{ background: Colors.blue.primary }} />

      <div className="flex flex-col gap-y-3">
        <div className="flex gap-x-2">
          <Title level={5} className="!w-[140px] shrink-0 !m-0 !text-[14px]">
            {t("selectMaterial")}
          </Title>
          {" - "}
          <Select
            loading={isFetchingMaterials}
            value={values.material_id}
            placeholder={t("selectMaterial")}
            options={adaptedMaterials}
            onChange={handleMaterialChange}
            onSearch={setMaterialSearch}
            className="w-[400px]"
            size="small"
            allowClear
            showSearch
            filterOption={false}
          />
        </div>

        <div className="flex gap-x-3">
          <div className="flex gap-x-2">
            <Title level={5} className="!w-[140px] shrink-0 !m-0 !text-[14px]">
              {t("startDate")}
            </Title>
            {" - "}
            <DatePicker
              className="w-[200px]"
              showTime={{ format: REPORT_TIME_FORMAT }}
              format={REPORT_DATE_TIME_FORMAT}
              size="small"
              value={
                values.timestamp_start ? dayjs(values.timestamp_start) : null
              }
              onChange={(val) => handleDateChange("timestamp_start", val)}
              onOk={(val) => handleDateChange("timestamp_start", val)}
            />
          </div>

          <Divider
            type="vertical"
            className="h-full"
            style={{ background: Colors.blue.primary }}
          />

          <div className="flex gap-x-2">
            <Title level={5} className="!m-0 !text-[14px]">
              {t("endDate")}
            </Title>
            {" - "}
            <DatePicker
              className="w-[200px]"
              showTime={{ format: REPORT_TIME_FORMAT }}
              format={REPORT_DATE_TIME_FORMAT}
              size="small"
              value={values.timestamp_end ? dayjs(values.timestamp_end) : null}
              onChange={(val) => handleDateChange("timestamp_end", val)}
              onOk={(val) => handleDateChange("timestamp_end", val)}
            />
          </div>
        </div>

        <ReportFormatSelect
          selectedReport={selectedReport}
          clearPdfUrl={clearPdfUrl}
          titleClassName="!w-[140px] shrink-0 !m-0 !text-[14px]"
        />
      </div>

      <Divider className="my-1.5" style={{ background: Colors.blue.primary }} />
    </>
  );
};

export { ConsumptionFilter };
